import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { listRequests, thumbUrl, updateRequest } from "./artGalleryApi";
import { REQUEST_KIND_LABEL, REQUEST_STATUSES, SITE_URL, STATUS_LABEL, formatDate, formatMoney } from "./artGalleryShared";
import { Segmented } from "./ArtGalleryFields";

const KIND_FILTERS = [{ id: "", label: "All types" }, ...Object.entries(REQUEST_KIND_LABEL).map(([id, label]) => ({ id, label }))];

const requestTotal = (request) =>
  request.items.every((i) => !i.priceOnRequest && i.price !== null) ? request.items.reduce((sum, i) => sum + (i.price || 0), 0) : null;

const RequestDetail = ({ request, onSaved, onError }) => {
  const [notes, setNotes] = useState(request.notes || "");
  const [busy, setBusy] = useState(false);

  const save = async (fields) => {
    setBusy(true);
    try {
      onSaved(await updateRequest(request._id, fields));
      if (fields.notes !== undefined) toast.success("Notes saved");
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };

  const address = [request.address?.line1, request.address?.line2, request.address?.town, request.address?.postcode].filter(Boolean);
  const total = requestTotal(request);
  const subject = encodeURIComponent(
    request.kind === "purchase" ? "Your purchase request - CLK Art" : request.kind === "viewing" ? "Your viewing request - CLK Art" : "Your enquiry - CLK Art"
  );

  return (
    <div className="ag-request-detail">
      <div className="ag-request-columns">
        <div>
          <h3>Customer</h3>
          <dl>
            <dt>Name</dt>
            <dd>{request.name}</dd>
            <dt>Email</dt>
            <dd>
              <a href={`mailto:${request.email}?subject=${subject}`}>{request.email}</a>
            </dd>
            {request.phone && (
              <>
                <dt>Phone</dt>
                <dd>
                  <a href={`tel:${request.phone}`}>{request.phone}</a>
                </dd>
              </>
            )}
            {request.preferredContact && (
              <>
                <dt>Prefers</dt>
                <dd>{request.preferredContact === "phone" ? "A phone call" : "Email"}</dd>
              </>
            )}
            {request.delivery && (
              <>
                <dt>Delivery</dt>
                <dd>{request.delivery === "collection" ? "Collection" : "Delivery"}</dd>
              </>
            )}
            {address.length > 0 && (
              <>
                <dt>Address</dt>
                <dd>{address.join(", ")}</dd>
              </>
            )}
          </dl>
          {request.message && (
            <>
              <h3>Message</h3>
              <p className="ag-request-message">{request.message}</p>
            </>
          )}
        </div>
        <div>
          <h3>{request.items.length ? `Artworks (${request.items.length})` : "Artworks"}</h3>
          {request.items.length ? (
            <ul className="ag-request-items">
              {request.items.map((item, index) => (
                <li key={`${item.slug}-${index}`}>
                  {item.imageUrl && <img src={thumbUrl(item.imageUrl, 240)} alt="" />}
                  <span>
                    <a href={`${SITE_URL}/artworks/${item.slug}`} target="_blank" rel="noreferrer">
                      {item.title}
                    </a>
                    <small>
                      {item.artistName}
                      {item.status && item.status !== "available" ? ` · was ${STATUS_LABEL[item.status]?.toLowerCase() || item.status} when requested` : ""}
                    </small>
                  </span>
                  <strong>{item.priceOnRequest ? "POA" : formatMoney(item.price)}</strong>
                </li>
              ))}
              {total !== null && request.items.length > 1 && (
                <li className="ag-request-total">
                  <span>Total</span>
                  <strong>{formatMoney(total)}</strong>
                </li>
              )}
            </ul>
          ) : (
            <p className="ag-muted">General enquiry - no artworks attached.</p>
          )}
          <h3>Status</h3>
          <Segmented options={REQUEST_STATUSES} value={request.status} onChange={(status) => save({ status })} full />
          <h3>Notes</h3>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Private notes for the team" />
          <div className="ag-request-actions">
            <span className="ag-muted">
              {request.emailed?.gallery ? "Emailed to the gallery" : "Gallery email not sent"} ·{" "}
              {request.emailed?.customer ? "confirmation sent to customer" : "no customer confirmation"}
            </span>
            <button className="ag-btn ag-btn-ghost" disabled={busy || notes === (request.notes || "")} onClick={() => save({ notes })}>
              Save notes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RequestsPanel = ({ onError, onCountChange }) => {
  const [status, setStatus] = useState("new");
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const result = await listRequests({ status: status || undefined, kind: kind || undefined, page, limit: 30 });
      setData(result);
      onCountChange(result.newCount);
    } catch (error) {
      onError(error);
    }
  }, [status, kind, page, onError, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const replace = (updated) => {
    setData((current) => ({ ...current, requests: current.requests.map((r) => (r._id === updated._id ? updated : r)) }));
    listRequests({ status: "new", limit: 1 })
      .then((r) => onCountChange(r.newCount))
      .catch(() => {});
  };

  return (
    <>
      <div className="ag-toolbar">
        <Segmented
          options={[...REQUEST_STATUSES, { id: "", label: "All" }]}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            setPage(1);
          }}
          aria-label="Type"
        >
          {KIND_FILTERS.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label}
            </option>
          ))}
        </select>
        <span className="ag-toolbar-spacer" />
        <button className="ag-btn ag-btn-ghost" onClick={load}>
          Refresh
        </button>
      </div>

      {!data ? (
        <div className="ag-empty">Loading requests...</div>
      ) : data.requests.length ? (
        <div className="ag-requests">
          {data.requests.map((request) => (
            <div key={request._id} className={`ag-request ${openId === request._id ? "is-open" : ""}`}>
              <button className="ag-request-row" onClick={() => setOpenId(openId === request._id ? null : request._id)}>
                <span className={`ag-badge ag-badge-req-${request.status}`}>{REQUEST_STATUSES.find((s) => s.id === request.status)?.label}</span>
                <span className="ag-request-who">
                  <strong>{request.name}</strong>
                  <small>{REQUEST_KIND_LABEL[request.kind]}</small>
                </span>
                <span className="ag-request-what">
                  {request.items.length ? request.items.map((i) => i.title).join(", ") : request.message.slice(0, 90)}
                </span>
                <span className="ag-request-when">{formatDate(request.createdAt)}</span>
              </button>
              {openId === request._id && <RequestDetail request={request} onSaved={replace} onError={onError} />}
            </div>
          ))}
        </div>
      ) : (
        <div className="ag-empty">{status === "new" ? "No new requests. Purchase requests and enquiries from the website appear here." : "No requests match."}</div>
      )}

      {data && data.pages > 1 && (
        <div className="ag-pager">
          <button className="ag-btn ag-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Newer
          </button>
          <span>
            Page {page} of {data.pages}
          </span>
          <button className="ag-btn ag-btn-ghost" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
            Older →
          </button>
        </div>
      )}
    </>
  );
};

export default RequestsPanel;
